const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

// Fetch wrapper to mimic axios functionality with timeout
const api = {
  async get(endpoint, options = {}) {
    const url = new URL(`${API_BASE_URL}/api${endpoint}`);
    
    // Add query parameters
    if (options.params) {
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined && options.params[key] !== null) {
          url.searchParams.append(key, options.params[key]);
        }
      });
    }

    console.log('API Request: GET', url.toString());

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 10000); // 10 second default timeout

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log('API Response:', response.status, url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data }; // Mimic axios response structure
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.error('API Timeout:', url.toString());
        throw new Error('Request timeout');
      }
      console.error('API Error:', error.message);
      throw error;
    }
  },

  async put(endpoint, data = {}, options = {}) {
    const url = `${API_BASE_URL}/api${endpoint}`;

    console.log('API Request: PUT', url);

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 10000); // 10 second default timeout

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log('API Response:', response.status, url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return { data: responseData }; // Mimic axios response structure
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.error('API Timeout:', url);
        throw new Error('Request timeout');
      }
      console.error('API Error:', error.message);
      throw error;
    }
  }
};

export const doctorService = {
  // Get all doctors with pagination and filtering
  getAll: (params = {}) => api.get('/doctors', { params }),
  
  // Get doctors by category
  getByCategory: (category, params = {}) => api.get('/doctors', { 
    params: { ...params, category } 
  }),
  
  // Get doctor by ID
  getById: (id) => api.get(`/doctors/${id}`),
  
  // Get all best doctors
  getBestDoctors: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'bestDoctors', limit: 100 } 
  }),
  
  // Get all Turkey doctors
  getTurkeyDoctors: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'turkeyDoctors', limit: 100 } 
  }),
  
  // Get all Thailand doctors
  getThailandDoctors: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'thailandDoctors', limit: 100 } 
  }),
  
  // Get all India doctors
  getIndiaDoctors: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'indiaDoctors', limit: 100 } 
  }),
  
  // Get all South Korea doctors
  getSouthKoreaDoctors: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'southKoreaDoctors', limit: 100 } 
  }),
  
  // Get star doctors India
  getStarDoctorsIndia: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'starDoctorsIndiaData', limit: 100 } 
  }),
  
  // Get star doctors Turkey
  getStarDoctorsTurkey: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'starDoctorsTurkeyData', limit: 100 } 
  }),
  
  // Get star doctors South Korea
  getStarDoctorsSouthKorea: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'starDoctorsSouthKoreaData', limit: 100 } 
  }),
  
  // Get star doctors Thailand
  getStarDoctorsThailand: (params = {}) => api.get('/doctors', { 
    params: { ...params, category: 'starDoctorsThailandData', limit: 100 } 
  }),
};

export const hospitalService = {
  getAll: (params = {}) => api.get('/hospitals', { params }),
  getById: (id) => api.get(`/hospitals/${id}`),
};

export const blogService = {
  getAll: (params = {}) => api.get('/blogs', { params }),
  getById: (id) => api.get(`/blogs/${id}`),
  getFeatured: (params = {}) => api.get('/blogs', { params: { ...params, featured: true } }),
};

export const newsService = {
  getAll: (params = {}) => api.get('/news', { params }),
  getById: (id) => api.get(`/news/${id}`),
  getBySlug: (slug) => api.get(`/news/slug/${slug}`),
  getFeatured: (params = {}) => api.get('/news', { params: { ...params, featured: true } }),
  getPublished: (params = {}) => api.get('/news', { params: { ...params, status: 'published' } }),
};

export const caseStudyService = {
  getAll: (params = {}) => api.get('/case-studies', { params }),
  getById: (id) => api.get(`/case-studies/${id}`),
  getFeatured: (params = {}) => api.get('/case-studies', { params: { ...params, featured: true } }),
};

export const testimonialService = {
  getAll: (params = {}) => api.get('/testimonials', { params }),
  getFeatured: (params = {}) => api.get('/testimonials', { params: { ...params, featured: true } }),
};

export const testimonialVideoService = {
  getAll: (params = {}) => api.get('/testimonial-videos', { params }),
  getFeatured: (params = {}) => api.get('/testimonial-videos', { params: { ...params, featured: true } }),
};

export const treatmentService = {
  getAll: (params = {}) => api.get('/treatments', { params }),
  getById: (id) => api.get(`/treatments/${id}`),
  getBySlug: (slug) => api.get(`/treatments/${slug}`),
  getFeatured: (params = {}) => api.get('/treatments', { params: { ...params, featured: true } }),
  getPublished: (params = {}) => api.get('/treatments', { params: { ...params, status: 'published' } }),
};

export const fundraiserService = {
  getAll: (params = {}) => api.get('/fundraisers', { params }),
  getById: (id) => api.get(`/fundraisers/${id}`),
  getActive: (params = {}) => api.get('/fundraisers', { params: { ...params, status: 'active' } }),
  recordDonation: (id, amount) => api.put(`/fundraisers/${id}/donation`, { amount }),
};

export default api;
